/******/ (() => { // webpackBootstrap
/******/ 	"use strict";

;// ./src/javascripts/utils.js
/**
 * Throttle execution of function
 *
 * @param {Function} callback function to be throttled
 * @param {number} interval milliseconds
 * @returns {Function}
 */
function throttle(callback, interval) {
  let enableCall = true;
  let calledWhenDisabled = false;


  return (...args) =>{
    const onTimeout = () => {
      if (calledWhenDisabled) {
        callback.apply(this, args);
        setTimeout(onTimeout, interval);
      } else {
        enableCall = true;
      }
      calledWhenDisabled = false;
    }

    if (!enableCall) {
      calledWhenDisabled = true;
      return;
    }

    enableCall = false;
    callback.apply(this, args);
    setTimeout(onTimeout, interval);
  }
}

/**
 * Take monetary value and format it as money
 *
 * @param {number | string} cents - Price in cents (hundreds of base unit)
 * @param {string} [format] - Format for money; store format will be used if undefined
 * @return {string} - HTML string with the formatted money value
 */
function formatMoney(cents, format) {
  var moneyFormat = format || '${{amount}}';
  if (typeof cents === 'string') {
    cents = cents.replace('.', '');
  }
  var value = '';
  var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  var formatString = moneyFormat;

  function formatWithDelimiters(number, precision, thousands, decimal) {
    if (precision === null || precision === undefined) {
      precision = 2;
    }
    thousands = thousands || ',';
    decimal = decimal || '.';

    if (isNaN(number) || number == null) {
      return '0';
    }

    number = (number / 100.0).toFixed(precision);

    var parts = number.split('.');
    var dollarsAmount = parts[0].replace(
      /(\d)(?=(\d{3})+(?!\d))/g,
      '$1' + thousands
    );
    var centsAmount = parts[1] ? decimal + parts[1] : '';

    return dollarsAmount + centsAmount;
  }

  var match = formatString.match(placeholderRegex);

  if (!match) {
    throw new Error(
      `Invalid format string: '${formatString}'. Expected '{{amount}}' or similar placeholders.`
    );
  }

  switch (match[1]) {
    case 'amount':
      value = formatWithDelimiters(cents, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_with_space_separator':
      value = formatWithDelimiters(cents, 2, ' ', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
    case 'amount_no_decimals_with_space_separator':
      value = formatWithDelimiters(cents, 0, ' ');
      break;
    default:
      throw new Error(`Unknown format type: ${match[1]}`);
  }

  return formatString.replace(placeholderRegex, value);
}

// FocusTrap class for managing focus within a container
class FocusTrap {
  /**
   * @param {HTMLElement} container - The container within which to trap focus.
   * @param {HTMLElement} [initialElement=null] - The element to focus initially. If not provided, the first focusable element is focused.
   * @param {Function} [onEscape=null] - Optional callback to execute when the Escape key is pressed.
   */
  constructor(container, initialElement = null, onEscape = null) {
    this.container = container;
    this.initialElement = initialElement;
    this.onEscape = onEscape;
    this.focusableElements = [];
    this.firstElement = null;
    this.lastElement = null;
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.updateFocusableElements = this.updateFocusableElements.bind(this);
    this.observer = new MutationObserver(this.updateFocusableElements);
    this.init();
  }

  /**
   * Initializes the focus trap by removing the 'inert' attribute,
   * updating focusable elements, setting initial focus, and attaching necessary event listeners.
   */
  init() {
    if (!this.container) return;

    // Remove the 'inert' attribute from the container
    this.container.removeAttribute('inert');

    this.updateFocusableElements();

    if (this.initialElement && this.isFocusable(this.initialElement)) {
      this.initialElement.focus();
    } else if (this.firstElement) {
      this.firstElement.focus();
    }

    this.container.addEventListener('keydown', this.handleKeyDown);
    this.observer.observe(this.container, { childList: true, subtree: true, attributes: true });
  }

  /**
   * Determines if an element is focusable.
   * @param {HTMLElement} el - The element to check.
   * @returns {boolean} - True if the element is focusable, else false.
   */
  isFocusable(el) {
    if (!el) return false;
    return (
      el.offsetParent !== null &&
      el.getAttribute('tabindex') !== '-1' &&
      !this.isInsideClosedDetails(el)
    );
  }

  /**
   * Determines if an element is inside a closed <details> element.
   * Allows <summary> elements to remain focusable even if their parent <details> is closed.
   * @param {HTMLElement} el - The element to check.
   * @returns {boolean} - True if inside a closed <details> and not a <summary>, else false.
   */
  isInsideClosedDetails(el) {
    let parent = el.parentElement;
    while (parent) {
      if (parent.tagName.toLowerCase() === 'details') {
        if (!parent.hasAttribute('open')) {
          // Allow the <summary> element itself to be focusable
          if (el.tagName.toLowerCase() === 'summary' && parent.querySelector('summary') === el) {
            return false;
          }
          return true;
        }
      }
      parent = parent.parentElement;
    }
    return false;
  }

  /**
   * Updates the list of focusable elements, excluding those inside closed <details> (except <summary>).
   */
  updateFocusableElements() {
    this.focusableElements = Array.from(this.container.querySelectorAll(
      'a[href], area[href], input:not([type=hidden]), select, textarea, button:not([disabled]), iframe, object, embed, [tabindex]:not([tabindex="-1"]), [contenteditable], summary'
    )).filter(el => this.isFocusable(el));

    if (this.focusableElements.length > 0) {
      this.firstElement = this.focusableElements[0];
      this.lastElement = this.focusableElements[this.focusableElements.length - 1];
    } else {
      this.firstElement = null;
      this.lastElement = null;
      console.warn('No focusable elements found within the container.');
    }
  }

  /**
   * Handles the keydown event to trap focus within the container.
   * @param {KeyboardEvent} e - The keyboard event.
   */
  handleKeyDown(e) {
    if (e.key === 'Tab') {
      if (this.focusableElements.length === 0) {
        e.preventDefault();
        return;
      }
      if (e.shiftKey) { // Shift + Tab
        if (document.activeElement === this.firstElement) {
          e.preventDefault();
          this.lastElement.focus();
        }
      } else { // Tab
        if (document.activeElement === this.lastElement) {
          e.preventDefault();
          this.firstElement.focus();
        }
      }
    } else if (e.key === 'Escape') {
      if (this.onEscape) {
        this.onEscape();
      }
    }
  }

  /**
   * Destroys the focus trap by removing event listeners and disconnecting observers.
   */
  destroy() {
    this.container.setAttribute('inert', '');
    this.container.removeEventListener('keydown', this.handleKeyDown);
    this.observer.disconnect();
  }
}

/**
 * CartManager - Centralized cart management utility for adding products to cart
 * Handles both single and multiple product additions with proper loading states,
 * cart drawer updates, and success messaging
 */
class CartManager {
  constructor() {
    this.cartType = document.getElementById('PageContainer')?.dataset.cartType;
    this.cartAction = document.getElementById('PageContainer')?.dataset.cartAction;
    this.languageUrl = document.getElementById('PageContainer')?.dataset.languageUrl;
    this.cartCountIndicator = document.querySelector('[data-cart-count-indicator]');
    
    // Get translations object
    this.wethemeGlobal = document.querySelector('script#wetheme-global');
    this.translations = this.wethemeGlobal ? JSON.parse(this.wethemeGlobal.textContent).translations : {};
  }

  /**
   * Add a single product to cart (equivalent to QuickAdd functionality)
   * @param {HTMLFormElement|FormData|Object} formOrData - Form element, FormData, or object with variant data
   * @param {HTMLElement} button - Button element for loading states
   * @param {Object} options - Additional options
   * @returns {Promise<Response>}
   */
  async addSingleProduct(formOrData, button, options = {}) {
    let variantId, quantity = 1;
    
    // Extract variant data based on input type
    if (formOrData instanceof HTMLFormElement) {
      const formData = new FormData(formOrData);
      variantId = formData.get('id');
      quantity = parseInt(formData.get('quantity') || '1', 10);
    } else if (formOrData instanceof FormData) {
      variantId = formOrData.get('id');
      quantity = parseInt(formOrData.get('quantity') || '1', 10);
    } else if (typeof formOrData === 'object') {
      variantId = formOrData.id || formOrData.variantId;
      quantity = formOrData.quantity || 1;
    }

    if (!variantId) {
      throw new Error('No variant ID provided');
    }

    const items = [{
      id: parseInt(variantId, 10),
      quantity
    }];

    return this.addItemsToCart(items, button, options);
  }

  /**
   * Add multiple products to cart
   * @param {Array} items - Array of {id, quantity} objects
   * @param {HTMLElement} button - Button element for loading states
   * @param {Object} options - Additional options
   * @returns {Promise<Response>}
   */
  async addMultipleProducts(items, button, options = {}) {
    if (!Array.isArray(items) || items.length === 0) {
      throw new Error('No items provided or invalid items array');
    }

    return this.addItemsToCart(items, button, options);
  }

  /**
   * Core method to add items to cart with fallback to individual additions
   * @param {Array} items - Array of {id, quantity} objects
   * @param {HTMLElement} button - Button element for loading states
   * @param {Object} options - Additional options
   * @returns {Promise<Response>}
   */
  async addItemsToCart(items, button, options = {}) {
    if (button) {
      this.showLoadingState(button);
    }

    try {
      // Try bulk add first
      const response = await this.performBulkAdd(items);
      
      if (response.ok) {
        await this.handleSuccess(button, response, options);
        return response;
      } else if (response.status === 422) {
        // Inventory error - try individual adds
        console.warn('Bulk add failed due to inventory constraints, trying individual adds...');
        const individualResponse = await this.addItemsIndividually(items);
        await this.handleSuccess(button, individualResponse, options);
        return individualResponse;
      } else {
        const errorData = await response.json();
        throw new Error(errorData.description || 'Failed to add items to cart');
      }
    } catch (error) {
      // If bulk add fails, try individual adds
      console.warn('Bulk add failed, trying individual adds...', error);
      try {
        const individualResponse = await this.addItemsIndividually(items);
        await this.handleSuccess(button, individualResponse, options);
        return individualResponse;
      } catch (individualError) {
        if (button) {
          this.hideLoadingState(button);
        }
        throw individualError;
      }
    }
  }

  /**
   * Perform bulk cart addition
   * @param {Array} items - Array of {id, quantity} objects
   * @returns {Promise<Response>}
   */
  async performBulkAdd(items) {
    const payload = { items };
    
    // Add sections parameter for cart drawer
    if (this.cartType === 'drawer') {
      payload.sections = 'cart-drawer';
    }
    
    const config = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(payload)
    };
    
    return fetch(window.routes.cart_add_url, config);
  }

  /**
   * Add items individually with error handling
   * @param {Array} items - Array of {id, quantity} objects
   * @returns {Promise<Response>}
   */
  async addItemsIndividually(items) {
    let lastSuccessfulResponse = null;
    let successfullyAdded = 0;
    let skippedItems = 0;

    for (const item of items) {
      try {
        const payload = { items: [item] };
        
        if (this.cartType === 'drawer') {
          payload.sections = 'cart-drawer';
        }
        
        const config = {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(payload)
        };
        
        const response = await fetch(window.routes.cart_add_url, config);
        
        if (response.ok) {
          successfullyAdded++;
          lastSuccessfulResponse = response;
        } else if (response.status === 422) {
          skippedItems++;
          console.warn(`Skipping item ${item.id} due to inventory constraints`);
        } else {
          const errorData = await response.json();
          console.error(`Failed to add item ${item.id}:`, errorData.description || 'Unknown error');
          skippedItems++;
        }
      } catch (error) {
        console.error(`Error adding item ${item.id}:`, error);
        skippedItems++;
      }
    }

    if (successfullyAdded === 0) {
      throw new Error('No items could be added to cart');
    }

    if (skippedItems > 0) {
      console.info(`Successfully added ${successfullyAdded} items, skipped ${skippedItems} items due to inventory or other issues`);
    }

    return lastSuccessfulResponse || { ok: true, status: 200 };
  }

  /**
   * Handle successful cart addition
   * @param {HTMLElement} button - Button element
   * @param {Response} response - Fetch response
   * @param {Object} options - Additional options
   */
  async handleSuccess(button, response, options = {}) {
    if (button) {
      this.hideLoadingState(button);
    }

    // Update cart drawer if needed
    if (this.cartType === 'drawer' && response && response.ok) {
      try {
        const responseJson = await response.clone().json();
        this.updateCartDrawer(responseJson);
      } catch (error) {
        // Response might already be consumed, continue without drawer update
        console.warn('Could not parse response for cart drawer update:', error);
      }
    }

    // Emit cart added event
    if (window.eventBus && options.sectionId) {
      window.eventBus.emit('cart:added', { sectionId: options.sectionId });
    }

    // Handle cart action and update count
    if (button) {
      this.handleCartAction(button);
    } else {
      this.updateCartCountIndicator();
    }
  }

  /**
   * Update cart drawer
   * @param {Object} responseJson - Cart response data
   */
  updateCartDrawer(responseJson) {
    if (window.eventBus) {
      window.eventBus.emit('update:cart:drawer', responseJson);
    }
  }

  /**
   * Handle cart action based on settings
   * @param {HTMLElement} button - Button element
   */
  handleCartAction(button) {
    if (this.cartType === 'drawer') {
      if (this.cartAction === 'show_added_message') {
        this.showAddedMessage(button);
        this.updateCartCountIndicator();
      } else if (this.cartAction === 'go_to_or_open_cart') {
        if (window.eventBus) {
          window.eventBus.emit('open:cart:drawer', { scrollToTop: true });
        }
        this.updateCartCountIndicator();
      }
    } else {
      if (this.cartAction === 'show_added_message') {
        this.showAddedMessage(button);
        this.updateCartCountIndicator();
      } else {
        window.location = window.routes.cart_url;
      }
    }
  }

  /**
   * Show "Added" message on button
   * @param {HTMLElement} button - Button element
   */
  showAddedMessage(button) {
    const addedTranslation = this.translations.added;
    
    // Check if this button uses the dynamic-section-button structure
    const buttonText = button.querySelector('[data-button-text]');
    const addedText = button.querySelector('[data-cart-added-text]');
    
    if (buttonText && addedText) {
      // Dynamic section button with added text support
      buttonText.style.visibility = 'hidden';
      addedText.style.display = 'block';
      
      setTimeout(() => {
        addedText.style.display = 'none';
        buttonText.style.visibility = '';
        button.disabled = false;
      }, 2000);
    } else if (buttonText) {
      // Dynamic section button without added text - create temporary overlay
      buttonText.style.visibility = 'hidden';
      
      const tempAddedText = document.createElement('span');
      tempAddedText.textContent = addedTranslation;
      tempAddedText.style.gridColumn = '1';
      tempAddedText.style.gridRow = '1';
      tempAddedText.classList.add('temp-added-text');
      button.appendChild(tempAddedText);
      
      setTimeout(() => {
        buttonText.style.visibility = '';
        tempAddedText.remove();
        button.disabled = false;
      }, 2000);
    } else {
      // Regular button - hide original content and overlay "Added" text
      const originalHTML = button.dataset.originalText || button.innerHTML;
      
      const originalContent = button.innerHTML;
      button.innerHTML = '';
      
      const hiddenContent = document.createElement('span');
      hiddenContent.innerHTML = originalContent;
      hiddenContent.style.visibility = 'hidden';
      hiddenContent.style.gridColumn = '1';
      hiddenContent.style.gridRow = '1';
      
      const addedContent = document.createElement('span');
      addedContent.textContent = addedTranslation;
      addedContent.style.gridColumn = '1';
      addedContent.style.gridRow = '1';
      addedContent.classList.add('temp-added-text');
      
      button.appendChild(hiddenContent);
      button.appendChild(addedContent);
      
      setTimeout(() => {
        button.innerHTML = originalHTML;
        button.disabled = false;
      }, 2000);
    }
  }

  /**
   * Update cart count indicator
   */
  async updateCartCountIndicator() {
    if (!this.cartCountIndicator) return;
    
    try {
      const languageParam = !this.languageUrl || this.languageUrl === '/' ? '' : this.languageUrl;
      const response = await fetch(`${languageParam}/cart?view=compare`);
      const cart = await response.json();
      if (window.wetheme?.updateCartCount) {
        window.wetheme.updateCartCount(cart);
      }
    } catch (error) {
      console.error('Error updating cart count:', error);
    }
  }

  /**
   * Show loading state on button
   * @param {HTMLElement} button - Button element
   */
  showLoadingState(button) {
    button.disabled = true;
    button.classList.add('loading');
    
    const buttonText = button.querySelector('[data-button-text]');
    const loadingIcon = button.querySelector('[data-loading-icon]');
    
    if (buttonText && loadingIcon) {
      // Dynamic section button with loading icon structure
      buttonText.style.visibility = 'hidden';
      loadingIcon.classList.remove('hidden');
    } else {
      // Regular button - store original text and show loading icon
      if (!button.dataset.originalText) {
        button.dataset.originalText = button.textContent;
      }
      button.innerHTML = `<svg viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="1.6em" height="1.6em" class="spin flex-full"><g clip-path="url(#clip0_3605_47041)"><path d="M12.5 23C6.42487 23 1.5 18.0751 1.5 12C1.5 5.92487 6.42487 1 12.5 1C18.5751 1 23.5 5.92487 23.5 12C23.5 15.1767 22.1534 18.0388 20 20.0468" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></g><defs><clipPath id="clip0_3605_47041"><rect width="24" height="24" fill="none" transform="translate(0.5)"/></clipPath></defs></svg>`;
    }
  }

  /**
   * Hide loading state on button
   * @param {HTMLElement} button - Button element
   */
  hideLoadingState(button) {
    button.disabled = false;
    button.classList.remove('loading');
    
    const buttonText = button.querySelector('[data-button-text]');
    const loadingIcon = button.querySelector('[data-loading-icon]');
    
    if (buttonText && loadingIcon) {
      // Dynamic section button with loading icon structure
      buttonText.style.visibility = '';
      loadingIcon.classList.add('hidden');
    } else {
      // Regular button - restore original text
      if (button.dataset.originalText) {
        button.innerHTML = button.dataset.originalText;
      }
    }
  }
}

;// ./src/unique_sections/quiz/quiz.js


class Quiz extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.getAttribute('data-section-id');
    this.productsToShow = parseInt(this.getAttribute('data-products-to-show'), 10) || 3;
    this.selectedTags = [];
    this.selectedButtonIndex = [];
    this.productPool = [];
    this.fallbackPool = [];
    this.currentQuestionIndex = 0;
    this.totalQuestions = 0;
    this.init = this.init.bind(this);
    this.parseProductPools = this.parseProductPools.bind(this);
    this.fetchProductData = this.fetchProductData.bind(this);
    this.selectOption = this.selectOption.bind(this);
    this.highlightSelectedOption = this.highlightSelectedOption.bind(this);
    this.goBack = this.goBack.bind(this);
    this.handleQuickAddClick = this.handleQuickAddClick.bind(this);
    this.showQuestion = this.showQuestion.bind(this);
    this.applyOptionExclusion = this.applyOptionExclusion.bind(this);
    this.hideAllQuestions = this.hideAllQuestions.bind(this);
    this.updateProgressIndicator = this.updateProgressIndicator.bind(this);
    this.setOverlayDarkness = this.setOverlayDarkness.bind(this);
    this.computeMatches = this.computeMatches.bind(this);
    this.renderResults = this.renderResults.bind(this);
    this.setResultsHeading = this.setResultsHeading.bind(this);
    this.showResults = this.showResults.bind(this);
    this.showLoadingOverlay = this.showLoadingOverlay.bind(this);
    this.hideLoadingOverlay = this.hideLoadingOverlay.bind(this);
    this.resetQuizState = this.resetQuizState.bind(this);
    this.showPlaceholderProductCards = this.showPlaceholderProductCards.bind(this);
    this.retakeQuiz = this.retakeQuiz.bind(this);
    this.handleSectionUnload = this.handleSectionUnload.bind(this);
    this.handleSectionSelect = this.handleSectionSelect.bind(this);
    this.handleBlockSelect = this.handleBlockSelect.bind(this);
    this.handleReload = this.handleReload.bind(this);
    this.cartManager = new CartManager();
  }

  connectedCallback() {
    window.wetheme.webcomponentRegistry.register({key: 'component-quiz'});
    this.init();
  }

  init() {
    this.parseProductPools();

    // Add event listeners to buttons
    const buttons = this.querySelectorAll('[data-quiz-button]');
    buttons.forEach(button => {
      button.addEventListener('click', this.selectOption);
    });

    const backButtons = this.querySelectorAll('[data-quiz-back]');
    backButtons.forEach(button => {
      button.addEventListener('click', this.goBack);
    });

    const retakeButtons = this.querySelectorAll('[data-quiz-retake]');
    retakeButtons.forEach(button => {
      button.addEventListener('click', this.retakeQuiz);
    });

    this.totalQuestions = this.querySelectorAll('[data-quiz-question]').length;

    // Initialize quiz state - show only first/current question
    if (window.Shopify.designMode) {
      this.currentQuestionIndex = parseInt(document.body.getAttribute('data-quiz-block-load'), 10) || 0;
      // Reset state
      setTimeout(() => {
        document.body.removeAttribute('data-quiz-block-load');
      }, 1000);
    }
    this.hideAllQuestions();
    this.showQuestion(this.currentQuestionIndex, 'none');
    this.updateProgressIndicator();

    if (window.Shopify.designMode) {
      window.addEventListener('shopify:section:unload', this.handleSectionUnload);
      window.addEventListener('shopify:section:select', this.handleSectionSelect);
      window.addEventListener('shopify:block:select', this.handleBlockSelect);
    }
  }

  parseProductPools() {
    const poolScript = this.querySelector('[data-quiz-product-pool]');
    const fallbackScript = this.querySelector('[data-quiz-fallback-pool]');

    try {
      this.productPool = poolScript ? JSON.parse(poolScript.textContent) : [];
    } catch (error) {
      console.error('Quiz: Error parsing product pool', error);
      this.productPool = [];
    }

    try {
      this.fallbackPool = fallbackScript ? JSON.parse(fallbackScript.textContent) : [];
    } catch (error) {
      console.error('Quiz: Error parsing fallback pool', error);
      this.fallbackPool = [];
    }
  }

  hideAllQuestions() {
    const questions = this.querySelectorAll('[data-quiz-question]');
    const results = this.querySelector('[data-quiz-product-cards]');

    questions.forEach(question => {
      question.classList.add('is-hidden');
      question.classList.remove('quiz__question--active', 'quiz__question--enter', 'quiz__question--enter-reverse', 'quiz__question--exit', 'quiz__question--exit-reverse');
    });

    if (results) {
      results.classList.add('is-hidden');
    }
  }

  applyOptionExclusion(questionEl, questionIndex) {
    const previousTag = this.selectedTags[questionIndex - 1];
    const buttons = questionEl.querySelectorAll('[data-quiz-button]');
    buttons.forEach(button => {
      const isExcluded = Boolean(previousTag) && button.getAttribute('data-quiz-tag') === previousTag;
      button.classList.toggle('is-hidden', isExcluded);
    });
  }

  showQuestion(index, direction = 'forward') {
    const questions = this.querySelectorAll('[data-quiz-question]');
    const next = questions[index];
    if (!next) return;

    this.applyOptionExclusion(next, index);

    if (direction === 'none') {
      questions.forEach(question => {
        question.classList.add('is-hidden');
        question.classList.remove('quiz__question--active', 'quiz__question--enter', 'quiz__question--enter-reverse', 'quiz__question--exit', 'quiz__question--exit-reverse');
      });
      next.classList.remove('is-hidden');
      next.classList.add('quiz__question--active');
      return;
    }

    const current = Array.from(questions).find(question => !question.classList.contains('is-hidden'));

    if (current && current !== next) {
      current.classList.remove('quiz__question--active');
      current.classList.add(direction === 'backward' ? 'quiz__question--exit-reverse' : 'quiz__question--exit');
      current.addEventListener('transitionend', () => {
        current.classList.add('is-hidden');
        current.classList.remove('quiz__question--exit', 'quiz__question--exit-reverse');
      }, { once: true });
    }

    next.classList.remove('is-hidden');
    next.classList.add(direction === 'backward' ? 'quiz__question--enter-reverse' : 'quiz__question--enter');
    // Force reflow so the enter transition actually plays
    void next.offsetWidth;
    requestAnimationFrame(() => {
      next.classList.add('quiz__question--active');
      next.classList.remove('quiz__question--enter', 'quiz__question--enter-reverse');
    });
  }

  updateProgressIndicator() {
    const progressEl = this.querySelector('[data-quiz-progress]');
    if (!progressEl) return;

    progressEl.classList.remove('is-hidden');

    const textEl = this.querySelector('[data-quiz-progress-text]');
    const fillEl = this.querySelector('[data-quiz-progress-fill]');
    const step = this.currentQuestionIndex + 1;
    const total = this.totalQuestions || this.querySelectorAll('[data-quiz-question]').length;
    const translations = this.cartManager.translations || {};

    if (textEl) {
      const template = translations.quizProgressLabel || 'Step [step] of [total]';
      textEl.textContent = template.replace('[step]', step).replace('[total]', total);
    }

    if (fillEl && total > 0) {
      fillEl.style.width = `${(step / total) * 100}%`;
    }

    this.setOverlayDarkness(total > 0 ? Math.max(0, 1 - (this.currentQuestionIndex / total)) : 1);
  }

  setOverlayDarkness(alpha) {
    const overlay = document.getElementById('QuizOverlay');
    if (overlay) {
      overlay.style.setProperty('--quiz-overlay-progress', alpha);
    }
  }

  showResults() {
    const progressEl = this.querySelector('[data-quiz-progress]');
    if (progressEl) {
      progressEl.classList.add('is-hidden');
    }

    const results = this.querySelector('[data-quiz-product-cards]');
    if (!results) return;
    results.classList.remove('is-hidden');

    const productCards = results.querySelectorAll('[data-quiz-product-card]');
    if (productCards.length > 0) {
      productCards.forEach(card => {
        card.setAttribute('data-animate', true);
      });
    }
  }

  showLoadingOverlay() {
    const overlay = this.querySelector('[data-quiz-loading]');
    if (overlay) {
      overlay.classList.add('is-active');
    }
  }

  hideLoadingOverlay() {
    const overlay = this.querySelector('[data-quiz-loading]');
    if (overlay) {
      overlay.classList.remove('is-active');
    }
  }

  resetQuizState() {
    // Reset selections and current question index
    this.selectedTags = [];
    this.selectedButtonIndex = [];
    this.currentQuestionIndex = 0;

    // Remove loading/selected state from all buttons
    const buttons = this.querySelectorAll('[data-quiz-button]');
    buttons.forEach(button => {
      button.classList.remove('is-loading', 'is-selected');
    });

    // Hide placeholder product cards
    const placeholderContainer = this.querySelector('[data-quiz-product-card-placeholders]');
    if (placeholderContainer) {
      placeholderContainer.classList.add('is-hidden');
    }
  }

  attachQuickAddEvents() {
    const quickAddButtons = this.querySelectorAll('[data-quick-add-button]');
    quickAddButtons.forEach(button => {
      button.addEventListener('click', this.handleQuickAddClick);
    });
  }

  async handleQuickAddClick(event) {
    event.preventDefault();
    const button = event.currentTarget;
    const form = button.closest('form');

    if (!form) return;

    try {
      await this.cartManager.addSingleProduct(form, button, {
        sectionId: this.sectionId
      });

    } catch (error) {
      console.error('Error adding product to cart:', error);
    }
  }

  selectOption(event) {
    const button = event.currentTarget;
    const questionEl = button.closest('[data-quiz-question]');
    if (!questionEl) return;

    const questionIndex = parseInt(questionEl.getAttribute('data-question-index'), 10);
    const optionIndex = parseInt(button.getAttribute('data-option-index'), 10);
    const tag = button.getAttribute('data-quiz-tag') || '';
    const isLastQuestion = questionIndex === this.totalQuestions - 1;

    // Overwrite (not accumulate) so navigating Back and re-answering replaces the prior choice
    this.selectedTags[questionIndex] = tag;
    this.selectedButtonIndex[questionIndex] = optionIndex;

    this.highlightSelectedOption(questionEl, optionIndex);
    button.classList.add('is-loading');

    // Delay to simulate loading
    setTimeout(() => {
      button.classList.remove('is-loading');

      if (isLastQuestion) {
        questionEl.classList.add('is-hidden');
        this.showLoadingOverlay();

        setTimeout(() => {
          this.hideLoadingOverlay();
          this.renderResults();
        }, 1500);
      } else {
        this.currentQuestionIndex++;
        this.showQuestion(this.currentQuestionIndex, 'forward');
        this.updateProgressIndicator();
      }
    }, 800);
  }

  highlightSelectedOption(questionEl, optionIndex) {
    const buttons = questionEl.querySelectorAll('[data-quiz-button]');
    buttons.forEach((button, index) => {
      button.classList.toggle('is-selected', index === optionIndex);
    });
  }

  goBack() {
    if (this.currentQuestionIndex === 0) return;

    const previousIndex = this.currentQuestionIndex - 1;
    this.currentQuestionIndex = previousIndex;
    this.showQuestion(previousIndex, 'backward');
    this.updateProgressIndicator();

    const questions = this.querySelectorAll('[data-quiz-question]');
    const previousQuestionEl = questions[previousIndex];
    const previousSelectedIndex = this.selectedButtonIndex[previousIndex];
    if (previousQuestionEl && previousSelectedIndex !== undefined && previousSelectedIndex !== null) {
      this.highlightSelectedOption(previousQuestionEl, previousSelectedIndex);
    }
  }

  computeMatches(selectedTags, pool, count) {
    const tags = selectedTags.filter(Boolean);

    if (tags.length === 0 || !pool || pool.length === 0) {
      return { scenario: 'fallback', products: [] };
    }

    const scored = pool.map(product => {
      const productTags = product.tags || [];
      const score = tags.reduce((total, tag) => total + (productTags.includes(tag) ? 1 : 0), 0);
      return { handle: product.handle, score };
    });

    scored.sort((a, b) => b.score - a.score);
    const topScore = scored[0] ? scored[0].score : 0;

    if (topScore === 0) {
      return { scenario: 'fallback', products: [] };
    }

    const top = scored.slice(0, count);
    // "Perfect" means the top product matched every scored question answered,
    // not a hardcoded count - not every question in the quiz necessarily scores.
    const scenario = topScore === tags.length ? 'perfect' : 'partial';
    return { scenario, products: top };
  }

  setResultsHeading(scenario) {
    const headingEl = this.querySelector('[data-quiz-results-heading]');
    if (!headingEl) return;

    const translations = this.cartManager.translations || {};
    const headingByScenario = {
      perfect: translations.quizPerfectMatchHeading,
      partial: translations.quizTopMatchesHeading,
      fallback: translations.quizFallbackHeading
    };

    const text = headingByScenario[scenario];
    if (text) {
      headingEl.textContent = text;
    }
  }

  async renderResults() {
    const match = this.computeMatches(this.selectedTags, this.productPool, this.productsToShow);
    let scenario = match.scenario;
    let handles = match.products.map(product => product.handle);

    if (scenario === 'fallback' || handles.length === 0) {
      scenario = 'fallback';
      handles = this.fallbackPool.map(product => product.handle).slice(0, this.productsToShow);
    }

    if (handles.length === 0) {
      // Both the scoring pool and the fallback collection are empty/unconfigured
      this.showPlaceholderProductCards();
      this.resetQuizState();
      return;
    }

    this.setResultsHeading(scenario);
    await this.fetchProductData(handles.join(','), scenario);
    this.showResults();
    this.resetQuizState();
  }

  async fetchProductData(productHandles, scenario) {
    const handles = productHandles.split(',').map(handle => handle.trim()).filter(handle => handle);
    const containers = this.querySelectorAll('[data-quiz-product-card]');

    let containerIndex = 0;

    for (const handle of handles) {
      if (containerIndex < containers.length) {
        try {
          const url = `/products/${handle}?section_id=quiz-product-card`;

          const response = await fetch(url);
          const html = await response.text();

          // Inject the HTML into the corresponding container
          containers[containerIndex].innerHTML = html;

          if (containerIndex === 0 && scenario === 'perfect') {
            const translations = this.cartManager.translations || {};
            const badge = document.createElement('span');
            badge.className = 'quiz__match-badge';
            badge.textContent = translations.quizPerfectMatchBadge || '';
            containers[containerIndex].prepend(badge);
          }

          containerIndex++;

          this.attachQuickAddEvents();
        } catch (error) {
          console.error(`Error fetching product card for ${handle}:`, error);
        }
      }
    }
  }

  showPlaceholderProductCards() {
    const container = this.querySelector('[data-quiz-product-card-placeholders]');
    if (container) {
      container.classList.remove('is-hidden');
    }
  }

  retakeQuiz() {
    const results = this.querySelector('[data-quiz-product-cards]');
    if (results) results.classList.add('is-hidden');

    const placeholders = this.querySelector('[data-quiz-product-card-placeholders]');
    if (placeholders) placeholders.classList.add('is-hidden');

    this.resetQuizState();
    this.hideAllQuestions();
    this.showQuestion(0, 'none');
    this.updateProgressIndicator();
  }

  handleSectionUnload(e) {
    const sectionId = e.detail.sectionId;
    if (this.sectionId != sectionId) return;
    window.eventBus.emit('quiz:modal:hide');
  }

  handleSectionSelect(e) {
    if (this.sectionId != e.detail.sectionId) return;
    this.handleReload();
  }

  handleBlockSelect(e) {
    const { sectionId, blockId } = e.detail;
    if (this.sectionId != sectionId || !blockId) return;
    const block = document.querySelector(`[data-block-id="${blockId}"]`);
    const questionIndex = block?.getAttribute('data-index') - 1 || 0;
    this.handleReload(questionIndex);
  }
  
  handleReload(questionIndex = 0) {
    // Keep track of the current block when the block is selected/loaded
    document.body.setAttribute('data-quiz-block-load', questionIndex);
  
    // Re-init the quiz on block select/reload
    this.resetQuizState();
    this.init();
  }

  disconnectedCallback() {
    const buttons = this.querySelectorAll('[data-quiz-button]');
    buttons.forEach(button => {
      button.removeEventListener('click', this.selectOption);
    });

    const backButtons = this.querySelectorAll('[data-quiz-back]');
    backButtons.forEach(button => {
      button.removeEventListener('click', this.goBack);
    });

    const retakeButtons = this.querySelectorAll('[data-quiz-retake]');
    retakeButtons.forEach(button => {
      button.removeEventListener('click', this.retakeQuiz);
    });

    const quickAddButtons = this.querySelectorAll('[data-quick-add-button]');
    quickAddButtons.forEach(button => {
      button.removeEventListener('click', this.handleQuickAddClick);
    });

    if (window.Shopify.designMode) {
      window.removeEventListener('shopify:section:unload', this.handleSectionUnload);
      window.removeEventListener('shopify:section:select', this.handleSectionSelect);
      window.removeEventListener('shopify:block:select', this.handleBlockSelect);
    }
  }
}

if (!customElements.get('quiz-section')) customElements.define('quiz-section', Quiz);

/******/ })()
;